/**
 * The garage floor's daily-quest card, as arithmetic.
 *
 * Which quest the box names, how full each segment of the day's bar is, and what
 * the countdown says are three questions with exactly one right answer each, and
 * all three are wrong in ways a screenshot cannot show: a box that names a
 * finished quest, a bar that disagrees with the counter above it, an "off by one
 * hour" clock. So they live here, pure and tested, and `DailyQuestBox.vue` draws
 * what it is handed — the split `hud-readout.ts` and `pan-zoom.ts` already
 * follow.
 *
 * Nothing here holds a copy of the quest pool: the wording, the targets and the
 * rewards are all the server's, exactly as `DailyQuestsWindow.vue` has them.
 */
import type { DailyQuest, DailyQuests } from '@/net/api';

/** How far along one quest is, 0 → 1. Already clamped by the server, clamped again. */
export function questFraction(quest: DailyQuest): number {
  if (quest.target <= 0) return quest.complete ? 1 : 0;
  return Math.max(0, Math.min(1, quest.progress / quest.target));
}

/**
 * One bar per quest, filled by that quest's own progress.
 *
 * Deliberately fractional rather than a row of lamps. A lamp per finished quest
 * says the same thing as the counter above it and nothing more; a part-filled
 * segment is the only place on this card that says *how close the day is*, which
 * is the reason to look at a bar at all.
 */
export function questSegments(quests: readonly DailyQuest[]): number[] {
  return quests.map(questFraction);
}

/** How many of today's are finished, whether or not the money has been taken. */
export function completedCount(quests: readonly DailyQuest[]): number {
  return quests.filter((q) => q.complete).length;
}

/**
 * Which quest the box names.
 *
 * **The one with the most progress**, because that is the one a player is closest
 * to being paid for and therefore the one worth being reminded of. A quest that
 * is finished and unpaid has a fraction of 1, so it wins on the same comparison
 * rather than needing a branch of its own — and its line then reads *ready to
 * claim*.
 *
 * With nothing started yet every candidate ties at zero, and the pick is
 * **seeded by the day** rather than taken with `Math.random()`. A card that
 * re-rolled on every render would change its own subject while somebody was
 * reading it, and one that re-rolled per mount would change it on every trip
 * back from the shop. Seeded by the day it is stable for as long as the set is,
 * and a different quest is featured tomorrow — which is what "random" is
 * actually for here.
 *
 * Already-claimed quests are never featured: the money is taken, so there is
 * nothing left to point at. All four claimed answers `null`, and the card says
 * so instead of naming one.
 */
export function featuredQuest(
  quests: readonly DailyQuest[],
  dayIndex: number,
): DailyQuest | null {
  const open = quests.filter((q) => !q.claimed);
  if (open.length === 0) return null;

  const best = open.reduce((a, b) => (questFraction(b) > questFraction(a) ? b : a));
  if (questFraction(best) > 0) return best;

  // Nothing begun. Stable for the day, different tomorrow.
  const index = ((dayIndex % open.length) + open.length) % open.length;
  return open[index] ?? null;
}

/** What the line under the featured quest's name is saying. */
export type FeaturedState = 'ready' | 'progress';

export function featuredState(quest: DailyQuest): FeaturedState {
  return quest.claimable ? 'ready' : 'progress';
}

/**
 * Seconds until the set rolls over, as a subtraction on read.
 *
 * `secondsUntilReset` is a remainder measured at the instant of the response —
 * the fuel gauge's shape, and the research countdown's — so the only clock
 * involved is the one that says how long ago that response arrived. Nothing
 * counts down anywhere.
 */
export function secondsUntilReset(
  quests: Pick<DailyQuests, 'secondsUntilReset'>,
  loadedAtMs: number,
  nowMs: number,
): number {
  if (!loadedAtMs) return Math.max(0, quests.secondsUntilReset);
  const elapsed = Math.floor((nowMs - loadedAtMs) / 1000);
  return Math.max(0, quests.secondsUntilReset - elapsed);
}

/**
 * `06:42:11`.
 *
 * Fixed width on purpose: the countdown sits at the right edge of the card's
 * header, and a label that shed a digit as it crossed an hour boundary would
 * step the row it is in once a day. Hours are padded but not capped, so a set
 * dealt a whole day out still reads honestly rather than wrapping to zero.
 */
export function countdownLabel(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
}
